"use strict";
/**
 * Helper functions for the de-fiskaly API
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformToSignV2UpsertTransactionData = exports.closingCashStatementFromTransactions = exports.closingHeadFromTransactions = exports.amountsPerVatFromLines = exports.transformToDEClosingDataLine = exports.transformToDEClosingDataAmountPerVatId = exports.transformToDEClosingDataPaymentType = exports.getFullAmountFromAmountsPerVatId = exports.getTotalPaid = exports.transformToCashPointClosingTransaction = void 0;
// Add as dependency
const dayjs = require("dayjs");
// Deep types auto generate by openApi
const de_closing_types_1 = require("./closing/de-closing.types");
const accounting_1 = require("./constants/accounting");
//  End deep types
const fiskaly_1 = require("./constants/fiskaly");
// We need this one also as dependency
const accounting_2 = require("./utils/accounting");
const de_transaction_types_1 = require("./interfaces/de-transaction.types");
/**
 * Helper function to transform complete transaction input (CompleteTransactionArgs)
 * to a Cash point closing transaction in
 * dsfinvk.d.ts (Paths.InsertCashPointClosing.RequestBody.transactions)
 * Here is an exmaple of a cash point closing transaction:
 * {
  "head": {
    "tx_id": "{{txId1}}",
    "transaction_export_id": "{{firstTransactionExportId}}",
    "closing_client_id": "{{clientId}}",
    "type": "Beleg",
    "storno": false,
    "number": 1,
    "timestamp_start": {{txTimeStart1}},
    "timestamp_end": {{$timestamp}},
    "user": {
      "user_export_id": "007",
      "name": "James"
    },
    "buyer": {
      "name": "Sammelkunde",
      "buyer_export_id": "1",
      "type": "Kunde"
    }
  },
  "data": {
    "full_amount_incl_vat": 21.42,
    "payment_types": [
      {
        "type": "Bar",
        "currency_code": "EUR",
        "amount": 21.42
      }
    ],
    "amounts_per_vat_id": [
      {
        "vat_definition_export_id": 1,
        "incl_vat": 21.42,
        "excl_vat": 18.00,
        "vat": 3.42
      }
    ],
    "lines": [
      {
        "business_case": {
          "type": "Umsatz",
          "amounts_per_vat_id": [
            {
              "vat_definition_export_id": 1,
              "incl_vat": 21.42,
              "excl_vat": 18,
              "vat": 3.42
            }
          ]
        },
        "lineitem_export_id": "1",
        "storno": false,
        "text": "Sonstiges",
        "item": {
          "number": "A",
          "quantity": 1.0,
          "price_per_unit": 18
        }
      }
    ]
  },
  "allocation_groups": [
    "Tisch 1",
    "Brunch Vegan"
  ],
  "security": {
    "tss_tx_id": "{{txId1}}"
  }
}
 */
function transformToCashPointClosingTransaction(input, transactionInfo) {
    // Build the transaciton head
    const head = {
        // Now we support only Beleg
        type: 'Beleg',
        // TODO Ensure that we support cancellation
        storno: false,
        number: input.seqId,
        // transformed ISO string to seconds
        timestamp_start: dayjs(transactionInfo.transactionStartTime).unix(),
        // transformed ISO string to seconds
        timestamp_end: dayjs(transactionInfo.transactionEndTime).unix(),
        tx_id: input.transactionUUID,
        transaction_export_id: String(input.seqId),
        closing_client_id: input.posRegisterId,
        // TODO: Ensure that we support references (Check email with fiskaly)
    };
    // In case we are dealing with a cancellation, we set the storno to true in the head,
    // and we add a reference to the original transaction. For now, since we could be dealing
    // with online transactions, we set the reference to the bookingUUID
    // Cancelled items have isCancelled set to 1.
    if (input.items.some((item) => item.isCancelled)) {
        head.storno = true;
        head.references = [
            {
                type: fiskaly_1.ClosingReferenceType.ExterneSonstige,
                name: 'bookingUUID',
                external_other_export_id: input.bookingUUID || '',
            },
        ];
    }
    // Data lines from input
    const lines = transformToDEClosingDataLine(input);
    // amounts per vat id from lines
    const amountsPerVatId = amountsPerVatFromLines(lines);
    // Build the transaction data
    const data = {
        // full_amount_incl_vat: getTotalPaid(input),
        full_amount_incl_vat: getFullAmountFromAmountsPerVatId(amountsPerVatId),
        payment_types: transformToDEClosingDataPaymentType(input),
        amounts_per_vat_id: amountsPerVatId,
        lines,
    };
    return {
        head,
        data,
        security: {
            tss_tx_id: input.transactionUUID,
        },
    };
}
exports.transformToCashPointClosingTransaction = transformToCashPointClosingTransaction;
/**
 * Total paid of a given complete transaction input
 */
function getTotalPaid(input) {
    let totalPaid = 0;
    // Filter out the gift card payments
    const payments = input.payments.filter((payment) => !accounting_1.VOUCHER_PAYMENT_METHODS.includes(payment.type));
    for (const payment of payments) {
        totalPaid += Number(payment.totalPaid);
    }
    return totalPaid;
}
exports.getTotalPaid = getTotalPaid;
/**
 * helper method to get full amount from amounts_per_vat_id
 */
function getFullAmountFromAmountsPerVatId(amountsPerVatId) {
    let fullAmount = 0;
    for (const amountPerVatId of amountsPerVatId) {
        fullAmount += amountPerVatId.incl_vat;
    }
    return fullAmount;
}
exports.getFullAmountFromAmountsPerVatId = getFullAmountFromAmountsPerVatId;
/**
 * Helper function to generate DEClosingDataPayementType array from ICompleteTransactionArgs
 * It uses the payments array from ICompleteTransactionArgs
 * @param input ICompleteTransactionArgs
 * @returns DEClosingDataPaymentType[]
 */
function transformToDEClosingDataPaymentType(input) {
    const paymentTypes = [];
    // Filter out voucher payments
    const payments = input.payments.filter((payment) => !accounting_1.VOUCHER_PAYMENT_METHODS.includes(payment.type));
    for (const payment of payments) {
        // if the payment type already exists, we add the amount to the existing amount
        // Otherwise, we push the payment type to the array
        // We use the fiskaly type of payment for comparision, cause smeetz payments are not the same as fiskaly payments
        const paymentFiskalyType = accounting_1.CASH_PAYMENT_METHODS.includes(payment.type)
            ? de_transaction_types_1.DEPaymentTypesEnum.CASH
            : de_transaction_types_1.DEPaymentTypesEnum.NON_CASH;
        const paymentTypeFound = paymentTypes.find((paymentType) => paymentType.type === paymentFiskalyType);
        if (paymentTypeFound) {
            // Refund payments should be nagative before adding to the total
            paymentTypeFound.amount += (0, accounting_2.isRefundPayment)(payment)
                ? -Number(payment.totalPaid)
                : Number(payment.totalPaid);
            continue;
        }
        const paymentType = {
            type: accounting_1.CASH_PAYMENT_METHODS.includes(payment.type)
                ? de_transaction_types_1.DEPaymentTypesEnum.CASH
                : de_transaction_types_1.DEPaymentTypesEnum.NON_CASH,
            // TODO: Ensure that we support other currencies
            currency_code: 'EUR',
            // Refund payments should be nagative before adding to the total
            amount: (0, accounting_2.isRefundPayment)(payment)
                ? -Number(payment.totalPaid)
                : Number(payment.totalPaid),
        };
        paymentTypes.push(paymentType);
    }
    // In case we have no payment types or all the payment types amount is 0,
    // we remove all the other payment types and we add a NON payment type with 0 amount
    if (!paymentTypes.length ||
        paymentTypes.every((paymentType) => paymentType.amount === 0)) {
        return [
            {
                type: de_transaction_types_1.DEPaymentTypesEnum.NON,
                currency_code: 'EUR',
                amount: 0,
            },
        ];
    }
    return paymentTypes;
}
exports.transformToDEClosingDataPaymentType = transformToDEClosingDataPaymentType;
/**
 * Helper function to generate DEClosingDataAmountPerVatId array from ICompleteTransactionArgs
 * It uses vats array (TransactionItemVatInput[]) from ICompleteTransactionArgs.items array
 * Vats that have the same rate and vat id are grouped together
 * @param input ICompleteTransactionArgs
 * @returns DEClosingDataAmountPerVatId[]
 */
function transformToDEClosingDataAmountPerVatId(input) {
    const amountsPerVatId = [];
    for (const item of input.items) {
        for (const vat of item.vats) {
            // item values for a given vat
            // make sure we multiply by quantity as per the fiskaly documentation
            // Check Beispiel 1.1 for reference (https://docs.google.com/spreadsheets/d/1QJOPKj0BV2zzPrTo-lmlnXlUJxyFjpgKQpIQa9em3l0/edit#gid=156883892)
            // And page 105 of DSFinV-k 2_3_0 Spezifikation
            const inclVat = item.priceValue * item.quantity;
            const vatAmount = Number(vat.itemTotalVAT);
            const exclVat = inclVat - vatAmount;
            const amountPerVatId = {
                vat_definition_export_id: vat.vatId,
                incl_vat: inclVat,
                excl_vat: exclVat,
                vat: vatAmount,
            };
            // If the vat id is already in the array, we add the amount to the existing amount
            // Otherwise, we push the amountPerVatId to the array
            const index = amountsPerVatId.findIndex((amountPerVatId) => amountPerVatId.vat_definition_export_id === vat.vatId);
            if (index !== -1) {
                amountsPerVatId[index].incl_vat += amountPerVatId.incl_vat;
                amountsPerVatId[index].excl_vat += amountPerVatId.excl_vat;
                amountsPerVatId[index].vat += amountPerVatId.vat;
                continue;
            }
            amountsPerVatId.push(amountPerVatId);
        }
    }
    return amountsPerVatId;
}
exports.transformToDEClosingDataAmountPerVatId = transformToDEClosingDataAmountPerVatId;
/**
 * Helper function to generate DEClosingDataLine array from ICompleteTransactionArgs
 * It uses items array from ICompleteTransactionArgs
 */
function transformToDEClosingDataLine(input) {
    const lines = [];
    // We use sequenceNumber to infer lineitem_export_id
    // we increment it for each line
    let sequenceNumber = 1;
    for (const item of input.items) {
        // Detect the business case
        // If we have a voucher id, we use EinzweckgutscheinKauf
        // Otherwise, we use Umsatz
        const businessCaseType = item.voucherId
            ? de_closing_types_1.BusinessCase.EinzweckgutscheinKauf
            : de_closing_types_1.BusinessCase.Umsatz;
        // item total price which is based on priceValue * quantity
        const itemTotal = item.priceValue * item.quantity;
        const line = {
            // infer lineitem_export_id from length of lines array
            lineitem_export_id: String(sequenceNumber),
            storno: false,
            text: String(item.categoryId),
            item: {
                number: String(item.priceId),
                quantity: item.quantity,
                price_per_unit: item.priceValue,
            },
            business_case: {
                type: businessCaseType,
                amounts_per_vat_id: item.vats.map((vat) => ({
                    vat_definition_export_id: vat.vatId,
                    // incl_vat: Number(item.total),
                    incl_vat: itemTotal,
                    excl_vat: parseFloat((itemTotal - vat.itemTotalVAT).toFixed(5)),
                    vat: vat.itemTotalVAT,
                })),
            },
            source_cash_register: input.posRegisterId,
        };
        // In case we are dealing with a cancelled item, we set the amounts_per_vat_id to negative
        // Items that are cancelled have isCancelled set to 1
        if (item.isCancelled) {
            for (const amountPerVatId of line.business_case.amounts_per_vat_id) {
                if (!amountPerVatId) {
                    continue;
                }
                amountPerVatId.incl_vat = -amountPerVatId.incl_vat;
                amountPerVatId.excl_vat = -amountPerVatId.excl_vat;
                amountPerVatId.vat = -amountPerVatId.vat;
            }
        }
        // Add the voucher id if it's present to the line
        if (item.voucherId) {
            line.voucher_id = item.voucherId;
        }
        lines.push(line);
        // Increment the sequence number
        sequenceNumber++;
    }
    // In case we have a gift card payment or discount payment, we add an extra line
    // for the gift card the Business case type is EinzweckgutscheinEinloesung and for
    // discount payment, the Business case type is Rabatt
    // The payment value will be transformed into an 'amounts_per_vat_id' array
    // with a single entry that has the vat id of 1 and the payment value as incl_vat
    // Since this is a single purpose voucher and we currently support only one vat id
    // which is 19% of the payment value, the vat will be 19% of the payment value
    // We also calculate the excl_vat by subtracting the vat from the incl_vat
    // Note that we save incl_vat and vat in negative inside the amounts_per_vat_id array
    // to indicate that this is a single purpose voucher redemption
    // For gift card, we add the voucher id to the line as voucher_id field.
    // Get the payments concerned with negative value lines
    const negativeLinePayments = input.payments.filter((payment) => accounting_1.VOUCHER_PAYMENT_METHODS.includes(payment.type) ||
        (0, accounting_2.isDiscountPayment)(payment));
    for (const negativeLinePayment of negativeLinePayments) {
        const businessCaseType = (0, accounting_2.isDiscountPayment)(negativeLinePayment)
            ? de_closing_types_1.BusinessCase.Rabatt
            : de_closing_types_1.BusinessCase.EinzweckgutscheinEinloesung;
        const line = {
            lineitem_export_id: String(lines.length + 1),
            storno: false,
            text: '',
            // the following voucher id is only needed for the gift card / voucher payment
            voucher_id: negativeLinePayment.promocodeId,
            item: {
                number: Math.random().toString(),
                quantity: 1,
                price_per_unit: Number(negativeLinePayment.totalPaid),
            },
            business_case: {
                type: businessCaseType,
                amounts_per_vat_id: [
                    // TODO: Ensure that we support other vat ids
                    {
                        vat_definition_export_id: 1,
                        incl_vat: -parseFloat(negativeLinePayment.totalPaid),
                        excl_vat: parseFloat((-(parseFloat(negativeLinePayment.totalPaid) -
                            parseFloat(negativeLinePayment.vatAmount))).toFixed(5)),
                        vat: -parseFloat(negativeLinePayment.vatAmount),
                    },
                ],
            },
            source_cash_register: input.posRegisterId,
        };
        lines.push(line);
    }
    // For cash transfer payments, we add an extra line with the business case type
    // Privateinlage for Cash in (represetned by positive totalPaid) and
    // Privatentnahme for Cash out (represetned by negative totalPaid)
    // The payment value will be transformed into an 'amounts_per_vat_id' array
    // no items are present in the line
    // The incl_vat and vat will be the payment value
    const cashTransferPayments = input.payments.filter((payment) => (0, accounting_2.isCashTransfer)(payment));
    for (const cashTransferPayment of cashTransferPayments) {
        const totalPaid = parseFloat(cashTransferPayment.totalPaid);
        const businessCaseType = totalPaid > 0 ? de_closing_types_1.BusinessCase.Privateinlage : de_closing_types_1.BusinessCase.Privatentnahme;
        const line = {
            lineitem_export_id: String(lines.length + 1),
            storno: false,
            text: '',
            item: undefined,
            business_case: {
                type: businessCaseType,
                amounts_per_vat_id: [
                    {
                        vat_definition_export_id: 5,
                        incl_vat: totalPaid,
                        excl_vat: totalPaid,
                        vat: 0,
                    },
                ],
            },
            source_cash_register: input.posRegisterId,
        };
        lines.push(line);
    }
    return lines;
}
exports.transformToDEClosingDataLine = transformToDEClosingDataLine;
/**
 * Helper function to generate DEClosingDataAmountPerVatId array from a given list of lines
 * It uses the business_case.amounts_per_vat_id array from each line
 * It groups the amounts_per_vat_id by vat_definition_export_id
 * @param lines
 */
function amountsPerVatFromLines(lines) {
    const vatIdMap = {};
    for (const line of lines) {
        if (!line.business_case) {
            continue;
        }
        for (const amountPerVatId of line.business_case.amounts_per_vat_id) {
            if (!amountPerVatId) {
                continue;
            }
            const vatId = amountPerVatId.vat_definition_export_id;
            if (vatIdMap[vatId]) {
                vatIdMap[vatId].incl_vat += amountPerVatId.incl_vat;
                vatIdMap[vatId].excl_vat += amountPerVatId.excl_vat;
                vatIdMap[vatId].vat += amountPerVatId.vat;
                continue;
            }
            vatIdMap[vatId] = Object.assign({}, amountPerVatId);
        }
    }
    // Ensure that all vats are rounded to 2 decimal places
    for (const vatId in vatIdMap) {
        vatIdMap[vatId].incl_vat = parseFloat(vatIdMap[vatId].incl_vat.toFixed(2));
        vatIdMap[vatId].excl_vat = parseFloat(vatIdMap[vatId].excl_vat.toFixed(2));
        vatIdMap[vatId].vat = parseFloat(vatIdMap[vatId].vat.toFixed(2));
    }
    return Object.values(vatIdMap);
}
exports.amountsPerVatFromLines = amountsPerVatFromLines;
/**
 * Helper method for creating DE cash point closing head from a given
 * list of transactions
 *
 * @param transactions List of transactions to create params from
 */
function closingHeadFromTransactions(transactions) {
    if (transactions.length < 1) {
        throw new Error('Invlid transactions array');
    }
    const firstTransaction = transactions[0];
    const lastTransaction = transactions[transactions.length - 1];
    if (!firstTransaction || !lastTransaction) {
        throw new Error('Invlid transactions array');
    }
    return {
        export_creation_date: dayjs().unix(),
        first_transaction_export_id: firstTransaction.uuid,
        last_transaction_export_id: lastTransaction.uuid,
    };
}
exports.closingHeadFromTransactions = closingHeadFromTransactions;
/**
 * Helper method for creating DE cash point closing cash_statement from a given
 * list of transactions
 * an example cash_statement:

  "cash_statement": {
    "business_cases": [
      {
        "type": "Umsatz",
        "amounts_per_vat_id": [
          {
              "vat_definition_export_id": 1,
              "incl_vat": 29.42,
              "excl_vat": 24.72,
              "vat": 4.7
          }
        ]
      }
    ],
    "payment": {
        "full_amount": 29.42,
        "cash_amount": 29.42,
        "cash_amounts_by_currency": [
            {
                "currency_code": "EUR",
                "amount": 29.42
            }
        ],
        "payment_types": [
            {
                "type": "Bar",
                "currency_code": "EUR",
                "amount": 29.42
            }
        ]
    }
  }
 * @param transactions List of transactions to create cash_statement from
 *
 */
function closingCashStatementFromTransactions(transactions) {
    // Business cases
    // Every line inside a transaction has a business case
    // We iterate over all the lines and group them by business case type.
    // We also calculate the total amount per vat id for each business case
    const businessCases = [];
    for (const transaction of transactions) {
        // skip if no lines are present
        if (!transaction.data.lines) {
            continue;
        }
        for (const line of transaction.data.lines) {
            const lineBusinessCase = line.business_case;
            // Skip if no business case is present
            if (!lineBusinessCase) {
                continue;
            }
            const index = businessCases.findIndex((businessCase) => businessCase.type === lineBusinessCase.type);
            if (index !== -1) {
                const businessCase = businessCases[index];
                for (const amountPerVatId of lineBusinessCase.amounts_per_vat_id) {
                    // skip if no amountPerVatId is not present
                    // this helps with type checking
                    if (!amountPerVatId) {
                        continue;
                    }
                    // Update the business case if it was found
                    // otherwise, insert it.
                    const index = businessCase.amounts_per_vat_id.findIndex((amount) => amount.vat_definition_export_id ===
                        amountPerVatId.vat_definition_export_id);
                    if (index !== -1) {
                        const amountsPerVatIdFound = businessCase.amounts_per_vat_id[index];
                        amountsPerVatIdFound.incl_vat += amountPerVatId.incl_vat;
                        amountsPerVatIdFound.excl_vat += amountPerVatId.excl_vat;
                        amountsPerVatIdFound.vat += amountPerVatId.vat;
                        continue;
                    }
                    businessCase.amounts_per_vat_id.push(amountPerVatId);
                }
                continue;
            }
            // Ensure that we have amounts_per_vat_id array that doesn't contain undefined
            businessCases.push({
                type: lineBusinessCase.type,
                // return only amounts that are not undefined
                amounts_per_vat_id: lineBusinessCase.amounts_per_vat_id.filter((amount) => !!amount),
            });
        }
    }
    // Build the payment object
    // Here is an example of a payment object:
    /**
       "payment": {
          "full_amount": 29.42,
          "cash_amount": 29.42,
          "cash_amounts_by_currency": [
              {
                  "currency_code": "EUR",
                  "amount": 29.42
              }
          ],
          "payment_types": [
              {
                  "type": "Bar",
                  "currency_code": "EUR",
                  "amount": 29.42
              }
          ]
      }
     */
    const payment = {
        full_amount: 0,
        cash_amount: 0,
        cash_amounts_by_currency: [],
        payment_types: [],
    };
    // Iterate over all the transactions and calculate the payment object
    for (const transaction of transactions) {
        // skip if no payment_types are present
        if (!transaction.data.payment_types) {
            continue;
        }
        for (const paymentType of transaction.data.payment_types) {
            // skip if no paymentType is present
            if (!paymentType) {
                continue;
            }
            // If the payment type is already in the array, we add the amount to the existing amount
            // Otherwise, we push the paymentType to the array
            const index = payment.payment_types.findIndex((paymentTypeFound) => paymentTypeFound.type === paymentType.type &&
                // For cash, we need to make sure that we have the same currency
                (paymentType.type !== de_transaction_types_1.DEPaymentTypesEnum.CASH ||
                    paymentTypeFound.currency_code === paymentType.currency_code));
            if (index !== -1) {
                const paymentTypeFound = payment.payment_types[index];
                paymentTypeFound.amount += paymentType.amount;
                continue;
            }
            payment.payment_types.push(paymentType);
        }
    }
    // Calculate the full_amount and cash_amount
    for (const paymentType of payment.payment_types) {
        payment.full_amount += paymentType.amount;
        if (paymentType.type === de_transaction_types_1.DEPaymentTypesEnum.CASH) {
            payment.cash_amount += paymentType.amount;
        }
    }
    // Calculate the cash_amounts_by_currency
    for (const paymentType of payment.payment_types) {
        if (paymentType.type === de_transaction_types_1.DEPaymentTypesEnum.CASH) {
            payment.cash_amounts_by_currency.push({
                currency_code: paymentType.currency_code,
                amount: paymentType.amount,
            });
        }
    }
    return {
        business_cases: businessCases,
        payment,
    };
}
exports.closingCashStatementFromTransactions = closingCashStatementFromTransactions;
/**
 * Transforms a cash point closing transaction to a complete transaction request body
 * needed for the fiskaly SIGN2 API
 * @param transaction Cash point closing transaction
 * @param clientId Client id
 * @returns SignV2UpsertTransactionData
 */
function transformToSignV2UpsertTransactionData(transaction, clientId) {
    const data = {
        client_id: clientId,
        state: 'FINISHED',
    };
    // Generate the schema
    data.schema = {
        standard_v1: {
            receipt: {
                receipt_type: 'RECEIPT',
                amounts_per_vat_rate: (transaction.data.amounts_per_vat_id || []).map((amountPerVatId) => ({
                    vat_rate: 
                    // return 0 for vat_definition_export_id 5 and 19 for vat_definition_export_id 1
                    amountPerVatId.vat_definition_export_id === 5
                        ? '0'
                        : amountPerVatId.vat_definition_export_id === 1
                            ? '19'
                            : '7',
                    amount: amountPerVatId.incl_vat.toFixed(2),
                })),
                amounts_per_payment_type: (transaction.data.payment_types || []).map((paymentType) => ({
                    payment_type: paymentType.type === de_transaction_types_1.DEPaymentTypesEnum.CASH
                        ? 'CASH'
                        : 'NON_CASH',
                    amount: paymentType.amount.toFixed(2),
                })),
            },
        },
    };
    data.state = 'FINISHED';
    data.client_id = clientId;
    return data;
}
exports.transformToSignV2UpsertTransactionData = transformToSignV2UpsertTransactionData;
//# sourceMappingURL=cert-common.js.map